import React from "react";

import { DB_COLLECTION_NAMES, StepDb, StepFrontend } from "@constants";
import {
  DEFAULT_COLLECTIONS_HOOK_RETURN,
  GetCollectionResp,
} from "../firestoreHookTypes";
import {
  CollectionConstraints,
  DEFAULT_COLLECTION_CONSTRAINTS,
  DEFAULT_PAGINATION_OPTS,
  PaginationOpts,
  useGetCollection,
} from "../useGetCollection";
import { convertTimestampToDate } from "@utils";

/**
 * Custom hook to fetch steps from Firestore.
 * @param {CollectionQueryComparator} comparators Query options
 */
export function useGetSteps(
  constraints: CollectionConstraints = DEFAULT_COLLECTION_CONSTRAINTS,
  paginationOpts: PaginationOpts = DEFAULT_PAGINATION_OPTS,
): GetCollectionResp<StepFrontend> {
  useGetCollection("steps");
  const stepsData = useGetCollection<StepDb>(
    DB_COLLECTION_NAMES.STEPS,
    constraints,
    paginationOpts,
  );
  if (stepsData.documents) {
    return {
      ...stepsData,
      documents: stepsData.documents.map((step) => ({
        ...step,
        timestamp: convertTimestampToDate(step.timestamp),
      })),
    };
  }
  return DEFAULT_COLLECTIONS_HOOK_RETURN;
}

/**
 * Wrapper for useGetSteps to fill in the comparators to query for steps only
 * associated with a specific module.
 * @param {string} moduleId ID of the module for which steps are being fetched.
 */
export function useGetStepsByModule(moduleId: string) {
  const constraints = React.useMemo<CollectionConstraints>(
    () => ({
      comparators: [
        {
          fieldOne: "moduleId",
          equalityComparator: "==",
          fieldTwo: moduleId,
        },
      ],
    }),
    [moduleId],
  );
  return useGetSteps(constraints);
}

/**
 * Wrapper for useGetSteps to fill in the comparators to query for steps only
 * associated with a specific user.
 * @param {string} userId ID of the user for which steps are being fetched.
 */
export function useGetStepsByUser(userId: string) {
  const constraints = React.useMemo<CollectionConstraints>(
    () => ({
      comparators: [
        {
          fieldOne: "userId",
          equalityComparator: "==",
          fieldTwo: userId,
        },
      ],
    }),
    [userId],
  );
  return useGetSteps(constraints);
}

/**
 * Wrapper for useGetSteps to fill in the comparators to query for steps only
 * associated with a specific component serial number.
 * @param {string} componentSerial serial of the user for which steps are being fetched.
 */
export function useGetStepsByComponent(componentSerial: string) {
  const constraints = React.useMemo<CollectionConstraints>(
    () => ({
      comparators: [
        {
          fieldOne: "mainComponentSerial",
          equalityComparator: "==",
          fieldTwo: componentSerial,
        },
      ],
    }),
    [componentSerial],
  );
  return useGetSteps(constraints);
}

/**
 * Wrapper for useGetSteps to fill in the comparators to query for retrieving
 * all steps of a specific type
 * @param {string} stepName: name of the step that is being fetched .
 */
export function useGetStepsByStepName(stepName: string) {
  const constraints = React.useMemo<CollectionConstraints>(
    () => ({
      comparators: [
        {
          fieldOne: "stepName",
          equalityComparator: "==",
          fieldTwo: stepName,
        },
      ],
    }),
    [stepName],
  );

  return useGetSteps(constraints);
}
